/**
 * 子パターン（#2967）のUI文言。
 *
 * 同じ読取パターンでも帳票の様式によって取得位置が変わる場合に、
 * 項目の取得ルールだけを上書きする設定を「子パターン」と呼ぶ。
 * コード上は `sub_pattern` を使う（明細の列を指す `children` と用語が衝突するため、
 * `child_pattern` は避ける）。
 */

import { SUB_PATTERN_NAME_MAX_LENGTH, SUB_PATTERN_DESCRIPTION_MAX_LENGTH } from './enum';

export const subPatternLabel = {
  feature: '子パターン',
  base: '標準',
  name: '子パターンの名称',
  // 仕分け判定に使う説明。空欄だと判定の手がかりが無く一度も選ばれないため必須
  description: 'この様式の特徴',
  descriptionHelper:
    'アップロードされた帳票がこの子パターンに当てはまるかを、AIがこの文章をもとに判定します。' +
    '他の様式と見分けられる特徴を書いてください。',
  descriptionPlaceholder: '例: 発注元が「株式会社サンプル」と印字されている注文書',
  overrideSection: '上書きする項目設定',
  overrideHelper:
    'チェックした項目だけ、この子パターン専用の設定で読み取ります。' +
    'チェックしていない項目は、読取パターンの設定のまま読み取ります。',
  // 取得ルールの入力欄のラベル。項目名の上書き欄と並ぶため、
  // どちらの欄か分かるように明示する
  ruleOverride: 'この帳票での取得ルール',
  // 帳票に印字されている見出しを入れる欄。内部的にはAIへ渡す項目名を差し替えるが、
  // 出力される列名は親のまま変わらない
  itemNameOverride: 'この帳票での項目名',
  itemNameOverrideHelper:
    '帳票に印字されている見出しが親パターンと違う場合に入力します' +
    '（例: 親が「得意先名」でも、この帳票では「発注元」と印字されている場合は「発注元」）。' +
    '出力される列名は変わりません。',
  // AI読取のON/OFF（#2968）の上書き。初期値は親の設定。
  // 名称は項目編集モーダルの「AI読取する」に揃える
  ocrTargetToggle: 'AI読取する',
  ocrTargetHelper: 'OFFの項目はAI読取の対象外になります',
  // 固定値の上書き（#3314）。値が取得できなかったときに入れる値を子パターン側で変える。
  // AI読取OFFの項目にも効く（読み取り後に空の項目へ入れる処理のため）
  fixedValueOverride: 'この帳票での固定値',
  fixedValueOverrideHelper:
    '値が取得できなかった場合にこの値を入れます。AI読取ONの場合は、読み取った値が優先されます。',
  // 上書きする前の取得ルール。参考として入力欄の下に小さく出す
  parentRulePrefix: '[参考：通常の取得ルール] ',
  // 上書きする前の固定値。取得ルールと同じ体裁で入力欄の下に出す
  parentFixedValuePrefix: '[参考：通常の固定値] ',
  // モーダル冒頭の説明。長い文章は読み飛ばされるため、
  // 「この帳票専用」という一点だけを太字で見せ、補足は1行に畳む
  introLead: 'この帳票の様式だけに使う取得ルールを登録します。',
  introSub: '項目は増減せず、取得ルールだけが差し替わります。他の様式には影響しません。',
  suggest: 'この子パターン用の取得ルールをAIで作成する',
  resuggest: 'この子パターン用の取得ルールをAIで作り直す',
  // 提案の前に値を直してもらう。AIは「正しい値」と帳票を見比べてルールを作るため、
  // ここを読み飛ばされると精度が落ちる。強調して表示する
  suggestNotice: '読み取り結果を正しい値に直し、下書き保存してから押してください。',
  // AIは保存済みの値を見るため、未保存だと直した内容が反映されない
  unsavedAlert: '保存されていない編集があります。下書き保存してから実行してください。',
  // 提案の対象を明示する。対象が分からないまま押すと戸惑うため
  changedItemsPrefix: '提案する項目：',
  suggestHelper:
    '修正後の値と帳票を見比べて、この様式に合った取得ルールを作成します。' +
    '対象は値を大きく直した項目のみです（直していない項目と、数文字の読み違いを直しただけの項目は現在のルールのまま読み取ります）。',
  // パターン一覧で子パターンの行の前に出す見出し（#3314）。
  // 親パターンの行と体裁が似ているため、何の一覧なのかを示す
  listSectionHeading: '子パターン',
  add: '子パターンを追加',
  create: '子パターンを作成',
  edit: '編集',
  editFromBadge: 'クリックすると、この子パターンの取得ルールを編集できます',
  // 再読み取り時の子パターン選択
  reOcrSelect: '読み取る子パターン',
  reOcrSelectHelper: '判定が誤っていた場合は、正しい子パターンを選んで読み直せます。',
  reOcrBaseOption: '標準（読取パターンの設定で読む）',
  delete: '削除',
  overrideCount: (count: number): string => `${count}項目を上書き`,
  filterAll: 'すべて',
} as const;

export const subPatternErrorMessage = {
  emptyName: `${subPatternLabel.name}を入力してください`,
  emptyDescription: `${subPatternLabel.description}を入力してください`,
  descriptionTooLong: `${subPatternLabel.description}は${SUB_PATTERN_DESCRIPTION_MAX_LENGTH}文字以内で入力してください`,
  nameTooLong: `${subPatternLabel.name}は${SUB_PATTERN_NAME_MAX_LENGTH}文字以内で入力してください`,
  duplicatedName: '同じ名称の子パターンがすでに登録されています',
  // 固定値・項目名・AI読取のON/OFFも上書きの対象に含まれるため、
  // 「取得ルール」に限定しない文言にする（#3314）
  emptyOverrides: '上書きする項目設定を1つ以上入力してください',
} as const;

/**
 * 子パターンのJSON入出力の文言（#3372）。
 *
 * 運営スタッフ限定の機能。親パターンの項目リストJSON（#2834 / #3039）と
 * 同じ言い回しにそろえる。
 */
export const subPatternJsonLabel = {
  copy: 'JSONでコピー',
  edit: 'JSONで編集',
  bulkImport: 'JSONで一括登録',
  // 3点メニューに出す文言（#3372）。子パターンが0件のときの導線なので、
  // 手段（JSON）ではなく目的（子パターンを登録する）で示す
  bulkImportMenu: '子パターン登録',
  copied: '子パターンのJSONをコピーしました。',
  copyFailed: 'クリップボードへのコピーに失敗しました。',
  nothingToCopy: 'コピーできる内容がありません。',
  editTitle: 'JSONで子パターンを編集する',
  bulkTitle: 'JSONで子パターンを一括登録する',
  editLead:
    'JSONを貼り付けると、この子パターンの設定に反映します。反映した時点では保存されないため、内容を確認してから保存してください。',
  bulkLead:
    '複数の子パターンをまとめて登録します。名称が同じ子パターンは上書き更新され、JSONに無い子パターンは残ります。',
  // 一括登録は複数件の書き方（[ ] で囲んで「,」で区切る）を示す。
  // 単体編集は1件しか反映できないため、配列にしない形の例を出す（#3372）。
  // どちらの例にも、上書きできる4種がひととおり出るようにしてある
  exampleHeadingBulk: '記入例（複数件は [ ] で囲んで「,」で区切る）',
  exampleHeadingSingle: '記入例',
  // 親パターンの設定JSON（#3372）。JSONは item_id で項目を指すが、
  // どの項目がどのIDなのかは画面の他の場所に出ていない。
  // 対応表を自前で組むより、親パターンのJSONをそのまま出したほうが
  // 型・取得ルール・選択肢まで分かり、AIへ渡す材料にもなる
  parentJsonHeading: 'この読取パターンの設定JSON',
  // コピーの導線は親パターンの設定JSONと記入例の両方で使う
  copyAction: 'コピー',
  copyActionDone: 'コピーしました',
  copyHint: 'クリックするとコピーできます',
  noItem: '上書きできる項目がありません。',
  placeholder: 'ここにJSONを貼り付けてください',
  // JSONには親パターンの項目名を持たないため、取り込み前に現在の項目名を見せる。
  // これが精査の材料になる
  targetHeading: '上書きの対象になる項目（現在の項目名）',
  unknownItem: '（この読取パターンに存在しない項目）',
  // 取り込みは差分適用ではなく全置換。1項目だけ書いたJSONを入れると他が消える
  replaceWarning:
    'JSONに書かれていない上書きは削除されます。現在の設定を残したい場合は、JSONに含めてください。',
  editApply: '反映する',
  bulkApply: '登録する',
  summary: (createCount: number, updateCount: number): string =>
    `新規 ${createCount}件 / 更新 ${updateCount}件`,
  bulkSucceeded: (count: number): string => `${count}件の子パターンを登録しました。`,
  // 途中で失敗しても巻き戻さない（syncHasSubPatterns を含むため単純な取り消しができない）。
  // 同じJSONを再度取り込めば、登録済みは名称一致で更新に回る
  bulkPartiallyFailed: (succeeded: number, total: number): string =>
    `${total}件のうち${succeeded}件を登録した時点で失敗しました。同じJSONをもう一度取り込むと、残りを登録できます。`,
} as const;

/** 削除確認ダイアログの文言 */
export const subPatternDeleteConfirm = {
  title: '子パターンを削除しますか？',
  body: 'この子パターンの取得ルールは使われなくなり、以降のアップロードは標準の設定で読み取られます。すでに読み取り済みの結果は変わりません。',
} as const;
